import React, { useState, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import './PDFViewer.css';
import axios from 'axios';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';
const API_KEY_STORAGE_KEY = 'validation_tool_api_key';

const PDFViewer = React.memo(function PDFViewer({ docType, docVersion, currentPage, fields, selectedField, onFieldClick, rules, conditionalRuleMode, unconditionalRuleMode }) {
  const [numPages, setNumPages] = useState(null);
  const [pageWidth, setPageWidth] = useState(null);
  const [scale, setScale] = useState(1.0);
  const [pageDimensions, setPageDimensions] = useState({ width: 0, height: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const containerRef = useRef(null);
  const pageRef = useRef(null);

  // Get API key from localStorage for PDF loading
  const apiKey = localStorage.getItem(API_KEY_STORAGE_KEY);

  // Memoize the file object to prevent unnecessary re-fetches
  const pdfFile = React.useMemo(() => ({
    url: `${API_BASE}/documents/${docType}/pdf${docVersion ? `?version=${encodeURIComponent(docVersion)}` : ''}`,
    httpHeaders: {
      'X-API-Key': apiKey
    }
  }), [docType, docVersion, apiKey]);

  useEffect(() => {
    if (containerRef.current) {
      const width = containerRef.current.offsetWidth;
      setPageWidth(Math.min(width - 40, 800));
    }
  }, []);

  // Update page dimensions when page loads or scale changes
  useEffect(() => {
    const updateDimensions = () => {
      if (pageRef.current) {
        const pageElement = pageRef.current.querySelector('.react-pdf__Page__canvas');
        if (pageElement) {
          setPageDimensions({
            width: pageElement.offsetWidth,
            height: pageElement.offsetHeight
          });
        }
      }
    };

    // Wait a bit for the page to render
    const timer = setTimeout(updateDimensions, 100);
    updateDimensions();

    return () => clearTimeout(timer);
  }, [currentPage, scale, pageWidth]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setIsLoading(false);
  };

  const onDocumentLoadStart = () => {
    setIsLoading(true);
  };

  // Reset loading state when document changes
  useEffect(() => {
    setIsLoading(true);
    setNumPages(null);
  }, [docType, docVersion]);

  const normalizeToPixels = (normalized, dimension) => {
    return normalized * dimension;
  };

  const handleFieldClick = React.useCallback((field, event) => {
    event.stopPropagation();
    onFieldClick(field);
  }, [onFieldClick]);

  const renderFieldOverlay = React.useCallback(() => {
    if (!pageRef.current || !pageWidth || !fields || fields.length === 0 || pageDimensions.width === 0) return null;

    const { width: actualPageWidth, height: actualPageHeight } = pageDimensions;

    return fields.map((field) => {
      if (!field.vertices || field.vertices.length !== 4) return null;

      const [x1, y1, x2, y2] = field.vertices;
      // Normalize coordinates (0-1 range) to actual pixel dimensions
      const left = normalizeToPixels(x1, actualPageWidth);
      const top = normalizeToPixels(y1, actualPageHeight);
      const width = normalizeToPixels(x2 - x1, actualPageWidth);
      const height = normalizeToPixels(y2 - y1, actualPageHeight);

      const isSelected = selectedField && selectedField.id === field.id;
      const isRequired = field.is_required || (rules && rules.required_fields && rules.required_fields[field.id]) || false;
      
      // Check if field is selected in conditional rule mode
      let conditionalModeClass = '';
      let conditionalModeStyle = {};
      if (conditionalRuleMode) {
        if (conditionalRuleMode.mode === 'condition_field' && conditionalRuleMode.selectedField === field.id) {
          conditionalModeClass = 'conditional-condition-selected';
          conditionalModeStyle = {
            border: '2px solid #ff9800',
            backgroundColor: 'rgba(255, 152, 0, 0.2)',
            boxShadow: '0 0 0 2px rgba(255, 152, 0, 0.3)'
          };
        } else if (conditionalRuleMode.mode === 'dependent_fields' && conditionalRuleMode.selectedFields?.includes(field.id)) {
          conditionalModeClass = 'conditional-dependent-selected';
          conditionalModeStyle = {
            border: '2px solid #4caf50',
            backgroundColor: 'rgba(76, 175, 80, 0.2)',
            boxShadow: '0 0 0 2px rgba(76, 175, 80, 0.3)'
          };
        } else if (conditionalRuleMode.mode === 'condition_field' || conditionalRuleMode.mode === 'dependent_fields') {
          conditionalModeClass = 'conditional-selectable';
          conditionalModeStyle = {
            border: '2px dashed #ff9800',
            backgroundColor: 'rgba(255, 152, 0, 0.1)',
            cursor: 'pointer'
          };
        }
      }
      
      // Check if field is selected in unconditional rule mode
      if (unconditionalRuleMode) {
        if (unconditionalRuleMode.mode === 'group_fields' && unconditionalRuleMode.selectedFields?.includes(field.id)) {
          conditionalModeClass = 'conditional-dependent-selected';
          conditionalModeStyle = {
            border: '2px solid #2196f3',
            backgroundColor: 'rgba(33, 150, 243, 0.2)',
            boxShadow: '0 0 0 2px rgba(33, 150, 243, 0.3)'
          };
        } else if (unconditionalRuleMode.mode === 'group_fields') {
          conditionalModeClass = 'conditional-selectable';
          conditionalModeStyle = {
            border: '2px dashed #2196f3',
            backgroundColor: 'rgba(33, 150, 243, 0.1)',
            cursor: 'pointer'
          };
        }
      }

      return (
        <div
          key={field.id}
          className={`field-overlay ${isSelected ? 'selected' : ''} ${isRequired ? 'required' : ''} ${conditionalModeClass}`}
          style={{
            position: 'absolute',
            left: `${left}px`,
            top: `${top}px`,
            width: `${width}px`,
            height: `${height}px`,
            border: conditionalModeStyle.border || (isSelected ? '2px solid #4a90e2' : '2px dashed #4a90e2'),
            backgroundColor: conditionalModeStyle.backgroundColor || (isSelected ? 'rgba(74, 144, 226, 0.1)' : 'rgba(74, 144, 226, 0.05)'),
            cursor: conditionalModeStyle.cursor || 'pointer',
            zIndex: isSelected ? 10 : 5,
            ...conditionalModeStyle
          }}
          onClick={(e) => handleFieldClick(field, e)}
          title={field.name}
        >
          {isSelected && !conditionalRuleMode && (
            <div className="field-tooltip">
              {field.name}
            </div>
          )}
          {conditionalRuleMode && conditionalRuleMode.mode === 'condition_field' && conditionalRuleMode.selectedField === field.id && (
            <div className="field-tooltip conditional-tooltip">
              Condition Field: {field.name}
            </div>
          )}
          {conditionalRuleMode && conditionalRuleMode.mode === 'dependent_fields' && conditionalRuleMode.selectedFields?.includes(field.id) && (
            <div className="field-tooltip conditional-tooltip">
              Dependent Field: {field.name}
            </div>
          )}
          {unconditionalRuleMode && unconditionalRuleMode.mode === 'group_fields' && unconditionalRuleMode.selectedFields?.includes(field.id) && (
            <div className="field-tooltip conditional-tooltip">
              Group Field: {field.name}
            </div>
          )}
        </div>
      );
    });
  }, [fields, pageDimensions, selectedField, rules, conditionalRuleMode, unconditionalRuleMode, handleFieldClick]);

  return (
    <div className="pdf-viewer-container" ref={containerRef}>
      <div className="pdf-controls">
        <button onClick={() => setScale(scale * 1.2)}>Zoom In</button>
        <button onClick={() => setScale(scale / 1.2)}>Zoom Out</button>
        <span>Scale: {(scale * 100).toFixed(0)}%</span>
      </div>
      <div className="pdf-viewer" style={{ position: 'relative' }}>
        {isLoading && (
          <div className="pdf-loading-overlay">
            <div>Loading PDF...</div>
          </div>
        )}
        <div style={{ display: isLoading ? 'none' : 'block' }}>
          <Document
            file={pdfFile}
            key={`${docType}-${docVersion || 'no-version'}`}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadStart={onDocumentLoadStart}
            loading={null}
            error={<div className="pdf-error">Error loading PDF. Please check your API key and try again.</div>}
          >
            <div ref={pageRef} style={{ position: 'relative', display: 'inline-block' }}>
              <Page
              pageNumber={currentPage}
              width={pageWidth}
              scale={scale}
              renderTextLayer={true}
              renderAnnotationLayer={true}
              onRenderSuccess={() => {
                // Update dimensions after page renders
                setTimeout(() => {
                  const pageElement = pageRef.current?.querySelector('.react-pdf__Page__canvas');
                  if (pageElement) {
                    setPageDimensions({
                      width: pageElement.offsetWidth,
                      height: pageElement.offsetHeight
                    });
                  }
                }, 50);
              }}
            />
            {renderFieldOverlay()}
          </div>
        </Document>
        </div>
      </div>
      <div className="page-indicator">
        Page {currentPage} of {numPages || '...'}
      </div>
    </div>
  );
});

export default PDFViewer;

